// Sdílená konfigurace navigace — používá ji sidebar, záložky oblastí, command
// palette (⌘K) i hlídání oprávnění podle URL (Layout → capForPath).
export type Capability = 'production' | 'finance' | 'clients' | 'rates' | 'team' | 'settings' | 'templates' | 'suppliers';
// Stránka. `cap` = vyžadované oprávnění; bez něj je viditelná všem přihlášeným.
export interface NavItem { label: string; to: string; icon: string; cap?: Capability }
// Oblast v sidebaru. Její stránky se ukazují jako záložky nahoře na stránce,
// sidebar má jen oblasti — dřív to bylo 22 položek v 6 sekcích.
export interface NavArea { id: string; label: string; tabs: NavItem[] }
// `color` se uplatní jen v motivu „barva podle modulu" (viz lib/theme.ts).
export interface NavSection { title: string; areas: NavArea[]; color?: string }

export const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Každodenní práce',
    color: '#2563eb',
    areas: [
      {
        id: 'prehled',
        label: 'Přehled',
        tabs: [
          { label: 'Přehled', to: '/', icon: '🏠', cap: 'production' },
          { label: 'AI asistent', to: '/asistent', icon: '✨', cap: 'finance' },
        ],
      },
      {
        id: 'prace',
        label: 'Moje práce',
        tabs: [
          { label: 'Moje stanice', to: '/stanice', icon: '🏭', cap: 'production' },
          { label: 'Docházka', to: '/dochazka', icon: '🕒', cap: 'production' },
          { label: 'Skenovat', to: '/skener', icon: '📷', cap: 'production' },
          { label: 'Postupy', to: '/znalosti', icon: '📘', cap: 'production' },
          { label: 'Modely', to: '/modely', icon: '🧊', cap: 'production' },
        ],
      },
      {
        // Zakázky mají vlastní přepínač pohledů (seznam / dílna / tabule /
        // harmonogram / kapacita) — viz features/work/views.ts.
        id: 'zakazky',
        label: 'Zakázky',
        tabs: [{ label: 'Zakázky', to: '/zakazky', icon: '📦', cap: 'production' }],
      },
    ],
  },
  {
    title: 'Provoz',
    color: '#7c3aed',
    areas: [
      {
        id: 'pujcovna',
        label: 'Půjčovna',
        tabs: [
          { label: 'Položky k pronájmu', to: '/pujcovna', icon: '🏷️', cap: 'finance' },
          { label: 'Rezervace', to: '/pujcovna/rezervace', icon: '📆', cap: 'finance' },
          { label: 'Poptávky', to: '/pujcovna/poptavky', icon: '📨', cap: 'finance' },
          { label: 'Vytížení', to: '/pujcovna/vytizeni', icon: '📈', cap: 'finance' },
        ],
      },
      {
        id: 'sklad',
        label: 'Sklad',
        tabs: [
          { label: 'Materiál', to: '/sklad', icon: '🧰', cap: 'production' },
          // Skener v telefonu: příjem / výdej / přesun / inventura materiálu.
          { label: 'Skenovat', to: '/sklad/sken', icon: '📷', cap: 'production' },
          { label: 'Pozice', to: '/sklad/pozice', icon: '🗄️', cap: 'production' },
          // Nákupní seznam po obchodech (odškrtává se v telefonu) a obchody.
          { label: 'Nákup', to: '/sklad/nakup', icon: '🛒', cap: 'production' },
          { label: 'Obchody', to: '/sklad/obchody', icon: '🏪', cap: 'production' },
          // Inventura kusů (štítky výrob a půjčovny) — materiál se inventuruje skenerem.
          { label: 'Inventura kusů', to: '/inventura', icon: '📋', cap: 'production' },
        ],
      },
      {
        // Produkční společnosti byly samostatný adresář se stejnými poli — sloučeno
        // do odběratelů, protože odběratel bývá právě ta produkční společnost.
        id: 'kontakty',
        label: 'Kontakty',
        tabs: [{ label: 'Odběratelé', to: '/klienti', icon: '🤝', cap: 'clients' }],
      },
    ],
  },
  {
    title: 'Řízení',
    color: '#b4522e',
    areas: [
      {
        id: 'reporty',
        label: 'Reporty',
        tabs: [
          { label: 'Reporty', to: '/reporty', icon: '📊', cap: 'finance' },
          { label: 'Faktury', to: '/reporty/faktury', icon: '🧾', cap: 'finance' },
        ],
      },
      {
        // Externí koordinátor: nabídky jménem dodavatelů a jejich vlastní adresáře,
        // oddělené od studiových (jiné oprávnění, jiné odběratele).
        id: 'koordinace',
        label: 'Koordinace',
        tabs: [
          { label: 'Nabídky dodavatelů', to: '/nabidky-dodavatelu', icon: '🧾', cap: 'suppliers' },
          { label: 'Dodavatelé', to: '/dodavatele', icon: '🛠️', cap: 'suppliers' },
          { label: 'Zákazníci dodavatelů', to: '/odberatele', icon: '🏢', cap: 'suppliers' },
        ],
      },
      {
        id: 'sprava',
        label: 'Správa',
        tabs: [
          { label: 'Lidé', to: '/tym', icon: '🙋', cap: 'team' },
          { label: 'Departmenty', to: '/departmenty', icon: '🏢', cap: 'clients' },
          { label: 'Šablony', to: '/sablony', icon: '🧩', cap: 'templates' },
          { label: 'Graf propojení', to: '/graf', icon: '🕸️', cap: 'production' },
          { label: 'Nastavení', to: '/nastaveni', icon: '⚙️', cap: 'settings' },
          { label: 'Záznam změn', to: '/zaznam-zmen', icon: '🕘', cap: 'settings' },
        ],
      },
    ],
  },
];

// Všechny stránky naplocho — pro ⌘K a hlídání oprávnění podle URL.
export const NAV_FLAT: NavItem[] = NAV_SECTIONS.flatMap((s) => s.areas.flatMap((a) => a.tabs));

// Vrátí jen položky, na které má uživatel s danými oprávněními nárok.
export const visibleItems = (items: NavItem[], caps: Capability[]): NavItem[] =>
  items.filter((i) => !i.cap || caps.includes(i.cap));

const matches = (pathname: string, to: string) =>
  to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(to + '/');

// Záložka, které cesta patří — nejdelší shoda (/pujcovna/rezervace, ne /pujcovna).
export function tabForPath(tabs: NavItem[], pathname: string): NavItem | undefined {
  return tabs.filter((t) => matches(pathname, t.to)).sort((a, b) => b.to.length - a.to.length)[0];
}

// Oblast, do které aktuální stránka patří (i její podstránky, např. detail).
export function areaForPath(pathname: string): { area: NavArea; section: NavSection } | undefined {
  let best: { area: NavArea; section: NavSection; len: number } | undefined;
  for (const section of NAV_SECTIONS) {
    for (const area of section.areas) {
      const tab = tabForPath(area.tabs, pathname);
      if (tab && (!best || tab.to.length > best.len)) best = { area, section, len: tab.to.length };
    }
  }
  return best && { area: best.area, section: best.section };
}

// Diakritika-insensitive normalizace pro vyhledávání ve štítcích.
export const norm = (s: string) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
